// Reunderwriting Module (Tier 3) PR 4 — Failures metrics section.
// Status breakdown + lane breakdown.

import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/design-system/components/ui";
import { CATEGORICAL, chartTheme } from "@/pages/dealFlow/metrics/chartTheme";
import { useReunderwritingFailuresMetrics } from "@/hooks/reunderwriting/useReunderwriting";
import type {
  ReunderwritingFailureStatusBucket,
  ReunderwritingLaneFailureBucket,
  ReunderwritingMetricsFilter,
} from "@/types/reunderwriting";

interface Props {
  filter: ReunderwritingMetricsFilter;
}

export function FailuresSection({ filter }: Props) {
  const navigate = useNavigate();
  const q = useReunderwritingFailuresMetrics(filter);

  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState />;
  const { openCount, reviewedCount, resolvedCount, byStatus, byLane } = q.data;

  return (
    <div className="flex flex-col gap-space-4" data-testid="reunderwriting-metrics-failures">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3">
        <Tile label="Open" value={openCount} />
        <Tile label="Reviewed" value={reviewedCount} />
        <Tile label="Resolved" value={resolvedCount} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-4">
        <Card>
          <CardHeader>
            <CardTitle>By status</CardTitle>
            <p className="text-small text-gray-mid mt-space-1">
              Click a bar to drill through to the filtered Failure Tracking list.
            </p>
          </CardHeader>
          <CardContent>
            {byStatus.every((b) => b.count === 0) ? (
              <EmptyState label="No failures in this window." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={byStatus} margin={{ left: 8, right: 8 }}>
                  <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
                  <XAxis dataKey="label" stroke={chartTheme.axis} fontSize={11} />
                  <YAxis stroke={chartTheme.axis} />
                  <Tooltip />
                  <Bar
                    dataKey="count"
                    cursor="pointer"
                    onClick={(entry) => {
                      const status = (entry as unknown as ReunderwritingFailureStatusBucket)
                        .status;
                      navigate(`/reunderwriting/failure-tracking?status=${status}`);
                    }}
                  >
                    {byStatus.map((d, i) => (
                      <Cell
                        key={d.status}
                        fill={CATEGORICAL[i % CATEGORICAL.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>By lane</CardTitle>
          </CardHeader>
          <CardContent>
            {byLane.length === 0 ? (
              <EmptyState label="No failures in this window." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={byLane} margin={{ left: 8, right: 8 }}>
                  <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
                  <XAxis dataKey="label" stroke={chartTheme.axis} fontSize={11} />
                  <YAxis stroke={chartTheme.axis} />
                  <Tooltip />
                  <Bar
                    dataKey="failureCount"
                    cursor="pointer"
                    onClick={(entry) => {
                      const lane = (entry as unknown as ReunderwritingLaneFailureBucket)
                        .lane;
                      navigate(`/reunderwriting/failure-tracking?lane=${lane}`);
                    }}
                  >
                    {byLane.map((d, i) => (
                      <Cell key={d.lane} fill={CATEGORICAL[i % CATEGORICAL.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent>
        <div className="text-label uppercase tracking-label text-gray-mid">{label}</div>
        <div className="font-display text-h2 text-onyx">{value}</div>
      </CardContent>
    </Card>
  );
}

function Loading() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-gray-mid">
      Loading failures metrics…
    </div>
  );
}

function ErrorState() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-status-error">
      Failed to load failures metrics.
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-[220px] items-center justify-center text-small text-gray-mid">
      {label}
    </div>
  );
}
